import React, { useEffect, useMemo, useState } from "react";
import BusinessLayout from "../../components/BusinessLayout";
import { useAuth } from "../../context/AuthProvider";
import {
    getBusinessById,
    getEmployeeByBusiness,
    getShiftsWeek,
    initShiftsWeekIfMissing,
    saveShiftsDraft,
    publishShiftsWeek,
} from "../../services/firestoreService";
import {
    startOfISOWeek,
    addWeeks,
    subWeeks,
    eachDayOfInterval,
    format,
    isSameDay,
} from "date-fns";

function isoWeekKey(date) {
    const weekYear = format(date, "RRRR");
    const weekNum = format(date, "II");
    return `${weekYear}-W${weekNum}`;
}
function mondayToSundayDates(weekStart) {
    const start = startOfISOWeek(weekStart);
    return eachDayOfInterval({ start, end: subWeeks(addWeeks(start, 1), 0) }).slice(0, 7);
}

export default function BusinessShiftPage() {
    const { userData, currentUser } = useAuth();
    const [biz, setBiz] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [weekStart, setWeekStart] = useState(startOfISOWeek(new Date()));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [shifts, setShifts] = useState(null);
    const [teamEnabled, setTeamEnabled] = useState(false);

    const weekKey = useMemo(() => isoWeekKey(weekStart), [weekStart]);
    const days = useMemo(() => mondayToSundayDates(weekStart), [weekStart]);
    const dayKeys = useMemo(() => days.map(d => format(d, "yyyy-MM-dd")), [days]);

    // role → weight map from business config
    const roleWeightByRole = useMemo(() => {
        const m = {};
        (biz?.config?.roles || []).forEach(r => {
            if (r.role) m[String(r.role).toLowerCase()] = Number(r.weight ?? 1);
        });
        return m;
    }, [biz]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            // 1) Load business + employees
            const business = await getBusinessById(userData.businessID);
            if (cancelled) return;
            setBiz(business);
            const teamEff = business?.teamEnabled ?? business?.config?.teamEnabled ?? false;
            setTeamEnabled(teamEff);

            const emps = await getEmployeeByBusiness(userData.businessID);
            if (cancelled) return;
            setEmployees(emps || []);

            if (!teamEff) {
                setShifts(null);
                setLoading(false);
                return;
            }

            // 2) Load or init week doc
            let wk = await getShiftsWeek(userData.businessID, weekKey);
            if (!wk) {
                wk = await initShiftsWeekIfMissing({
                    business,
                    businessID: userData.businessID,
                    weekKey,
                    dayKeys,
                });
            }
            if (cancelled) return;
            setShifts(wk);
            setLoading(false);
        })();
        return () => { cancelled = true; };
    }, [userData.businessID, weekKey]);

    const isPublished = shifts?.status === "PUBLISHED";

    // local state mutators (pure client; persist on Save/Publish)
    const upsertEntry = (dateKey, employeeID, patch) => {
        setShifts(prev => {
            const next = structuredClone(prev);
            const roster = next.days?.[dateKey] || [];
            const idx = roster.findIndex(r => r.employeeID === employeeID);
            if (idx === -1) {
                const emp = employees.find(e => e.id === employeeID);
                const role = (emp?.role || "").toLowerCase();
                const roleWeight = roleWeightByRole[role] ?? 1.0;
                roster.push({
                    employeeID,
                    role,
                    roleWeight,
                    start: null,
                    end: null,
                    hours: 0,
                    present: true,
                    note: "",
                    ...patch,
                });
            } else {
                roster[idx] = { ...roster[idx], ...patch };
            }
            next.days[dateKey] = roster;
            return next;
        });
    };
    const removeEntry = (dateKey, employeeID) => {
        setShifts(prev => {
            const next = structuredClone(prev);
            next.days[dateKey] = (next.days?.[dateKey] || []).filter(r => r.employeeID !== employeeID);
            return next;
        });
    };
    const markAllPresent = (dateKey, present) => {
        setShifts(prev => {
            const next = structuredClone(prev);
            next.days[dateKey] = (next.days?.[dateKey] || []).map(r => ({ ...r, present }));
            return next;
        });
    };
    const copyFromYesterday = (dateKey) => {
        const idx = dayKeys.indexOf(dateKey);
        if (idx <= 0) return;
        const prevKey = dayKeys[idx - 1];
        setShifts(prev => {
            const next = structuredClone(prev);
            next.days[dateKey] = (prev.days?.[prevKey] || []).map(r => ({ ...r }));
            return next;
        });
    };

    const handleSaveDraft = async () => {
        if (!shifts) return;
        setSaving(true);
        await saveShiftsDraft(userData.businessID, weekKey, shifts);
        setSaving(false);
        alert("Draft saved.");
    };
    const handlePublish = async () => {
        if (!shifts) return;
        const hasAny = Object.values(shifts.days || {}).some(arr => (arr || []).some(e => e.present));
        if (!hasAny) {
            alert("No present employees in this week.");
            return;
        }
        setSaving(true);
        await publishShiftsWeek(userData.businessID, weekKey, currentUser?.uid || "system");
        setShifts(prev => ({ ...prev, status: "PUBLISHED" }));
        setSaving(false);
        alert("Week published (locked).");
    };

    const previousWeek = () => setWeekStart(prev => subWeeks(prev, 1));
    const nextWeek = () => setWeekStart(prev => addWeeks(prev, 1));

    if (loading) {
        return (
            <BusinessLayout>
                <div className="max-w-5xl mx-auto py-8">Loading shifts…</div>
            </BusinessLayout>
        );
    }

    if (!teamEnabled) {
        return (
            <BusinessLayout>
                <div className="max-w-5xl mx-auto py-8">
                    <div className="p-4 border rounded-md bg-yellow-50 text-sm">
                        Team pool is <b>disabled</b>. Enable team tips in <span className="font-medium">Business Configuration</span> to use weekly shifts.
                    </div>
                </div>
            </BusinessLayout>
        );
    }

    return (
        <BusinessLayout>
            <div className="max-w-6xl mx-auto py-8 space-y-6">
                <div className="flex items-center justify-between">
                    <h1 className="text-2xl font-bold">Business Shifts — {weekKey}</h1>
                    <div className="flex items-center gap-2">
                        <button onClick={previousWeek} className="px-3 py-2 border rounded-md hover:bg-gray-50">← Prev</button>
                        <button onClick={nextWeek} className="px-3 py-2 border rounded-md hover:bg-gray-50">Next →</button>
                    </div>
                </div>

                <div className="text-sm text-gray-600">
                    Timezone: <b>{shifts?.timezone}</b> • Cutoff: <b>{shifts?.cutoffHourLocal}:00</b> • Status:{" "}
                    <b className={isPublished ? "text-green-700" : "text-yellow-700"}>{shifts?.status}</b>
                </div>

                {/* Days grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {dayKeys.map((dateKey, i) => {
                        const roster = shifts?.days?.[dateKey] || [];
                        const dayDate = days[i];
                        return (
                            <section key={dateKey} className="bg-white border rounded-lg p-4">
                                <div className="flex items-center justify-between mb-3">
                                    <h2 className="text-lg font-semibold">
                                        {format(dayDate, "EEE, MMM d")} {isSameDay(dayDate, new Date()) && <span className="text-xs text-blue-600 font-medium">(today)</span>}
                                    </h2>
                                    <div className="flex gap-2">
                                        <button className="text-xs px-2 py-1 border rounded hover:bg-gray-50" onClick={() => copyFromYesterday(dateKey)} disabled={isPublished}>
                                            Copy prev day
                                        </button>
                                        <button className="text-xs px-2 py-1 border rounded hover:bg-gray-50" onClick={() => markAllPresent(dateKey, true)} disabled={isPublished}>
                                            All present
                                        </button>
                                        <button className="text-xs px-2 py-1 border rounded hover:bg-gray-50" onClick={() => markAllPresent(dateKey, false)} disabled={isPublished}>
                                            All absent
                                        </button>
                                    </div>
                                </div>

                                {/* Add employee row */}
                                <div className="flex gap-2 mb-3">
                                    <select
                                        className="flex-1 p-2 border rounded-md"
                                        disabled={isPublished}
                                        onChange={(e) => {
                                            const employeeID = e.target.value;
                                            if (!employeeID) return;
                                            upsertEntry(dateKey, employeeID, { present: true });
                                            e.target.value = "";
                                        }}
                                        defaultValue=""
                                    >
                                        <option value="" disabled>Add employee…</option>
                                        {employees
                                            .filter(emp => !(roster || []).some(r => r.employeeID === emp.id))
                                            .map(emp => (
                                                <option key={emp.id} value={emp.id}>
                                                    {emp.displayName || emp.firstName || emp.email}
                                                </option>
                                            ))}
                                    </select>
                                </div>

                                {/* Roster table */}
                                <div className="overflow-x-auto">
                                    <table className="min-w-full text-sm">
                                        <thead>
                                        <tr className="text-left text-gray-700 border-b">
                                            <th className="py-2 pr-3">Employee</th>
                                            <th className="py-2 pr-3">Role</th>
                                            <th className="py-2 pr-3">Weight</th>
                                            <th className="py-2 pr-3">Hours</th>
                                            <th className="py-2 pr-3">Present</th>
                                            <th className="py-2 pr-3 text-right">Actions</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {roster.length === 0 && (
                                            <tr><td colSpan={6} className="py-3 text-gray-500">No entries yet.</td></tr>
                                        )}
                                        {roster.map((row) => {
                                            const emp = employees.find(e => e.id === row.employeeID);
                                            const name = emp?.displayName || emp?.firstName || emp?.email || row.employeeID;
                                            return (
                                                <tr key={row.employeeID} className="border-t">
                                                    <td className="py-2 pr-3">{name}</td>
                                                    <td className="py-2 pr-3 capitalize">{row.role || "—"}</td>
                                                    <td className="py-2 pr-3">{Number(row.roleWeight ?? 1).toFixed(1)}</td>
                                                    <td className="py-2 pr-3">
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="0.25"
                                                            className="w-24 p-1 border rounded"
                                                            value={Number(row.hours ?? 0)}
                                                            disabled={isPublished}
                                                            onChange={(e) =>
                                                                upsertEntry(dateKey, row.employeeID, {
                                                                    hours: Math.max(0, parseFloat(e.target.value || "0")),
                                                                })
                                                            }
                                                        />
                                                    </td>
                                                    <td className="py-2 pr-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={!!row.present}
                                                            disabled={isPublished}
                                                            onChange={(e) => upsertEntry(dateKey, row.employeeID, { present: e.target.checked })}
                                                        />
                                                    </td>
                                                    <td className="py-2 pr-3 text-right">
                                                        <button
                                                            className="text-xs px-2 py-1 border rounded hover:bg-gray-50"
                                                            onClick={() => removeEntry(dateKey, row.employeeID)}
                                                            disabled={isPublished}
                                                        >
                                                            Remove
                                                        </button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Totals */}
                                <div className="mt-2 text-xs text-gray-600">
                                    Present: {(roster || []).filter(r => r.present).length} • Total hours: {((roster || []).reduce((s, r) => s + (r.present ? Number(r.hours || 0) : 0), 0)).toFixed(2)}
                                </div>
                            </section>
                        );
                    })}
                </div>

                {/* Actions */}
                <div className="flex gap-3">
                    {!isPublished && (
                        <>
                            <button
                                onClick={handleSaveDraft}
                                disabled={saving}
                                className={`px-4 py-2 rounded-md border ${saving ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-50"}`}
                            >
                                {saving ? "Saving…" : "Save Draft"}
                            </button>
                            <button
                                onClick={handlePublish}
                                disabled={saving}
                                className={`px-4 py-2 rounded-md font-semibold ${saving ? "bg-gray-400 text-white cursor-not-allowed" : "bg-black text-white hover:bg-neutral-800"}`}
                            >
                                {saving ? "Locking…" : "Publish (Lock Week)"}
                            </button>
                        </>
                    )}
                    {isPublished && (
                        <div className="text-sm text-green-700">This week is published and locked. Edits are disabled.</div>
                    )}
                </div>

                <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 text-sm px-4 py-3 rounded-md">
                    Tip: Copy from the previous day, then tweak hours/presence to speed up planning.
                </div>
            </div>
        </BusinessLayout>
    );
}
